import { useEffect, useState } from "react";
import { getMyProfile } from "../../api/ProfileApis";
import ProfileUpdate from "./ProfileUpdate";
import PasswordChange from "./PasswordChange";
import Footer from "../../components/common/Footer";
import "./Profile.css";

function Profile() {
    const [profile, setProfile] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState("");
    const [isEditing, setIsEditing] = useState(false);
    const [isChangingPassword, setIsChangingPassword] = useState(false);

    useEffect(() => {
        const fetchProfile = async () => {
            try {
                setIsLoading(true);
                setError("");
                const response = await getMyProfile();
                setProfile(response.data);
            } catch (error) {
                setError('Failed to load profile.');
                
            } finally {
                setIsLoading(false);
            }
        };
        fetchProfile();
    }, []);

    const handleProfileUpdated = async () => {
        try {
            const response = await getMyProfile();
            setProfile(response.data);
            setIsEditing(false);
        } catch (error) {
            
        }
    };

    if (isLoading) {
        return (
            <div className="profile-page">
                <div className="profile-loading">
                    <div className="loading-spinner"></div>
                    <p>Loading...</p>
                </div>
            </div>
        );
    }

    if (error || !profile) {
        return (
            <div className="profile-page">
                <div className="profile-error">
                    <div className="error-icon">⚠️</div>
                    <h2>Oops!</h2>
                    <p>{error || "Profile not found"}</p>
                    <button onClick={() => window.location.reload()} className="retry-btn">
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    if (isEditing) {
        return (
            <div className="profile-page">
                <div className="profile-container">
                    <ProfileUpdate 
                        onProfileUpdated={handleProfileUpdated}
                        onCancel={() => setIsEditing(false)}
                    />
                </div>
                <Footer />
            </div>
        );
    }

    if (isChangingPassword) {
        return (
            <div className="profile-page">
                <div className="profile-container">
                    <PasswordChange 
                        onSuccess={() => setIsChangingPassword(false)}
                        onCancel={() => setIsChangingPassword(false)}
                    />
                </div>
                <Footer />
            </div>
        );
    }

    return (
        <div className="profile-page">
            <div className="profile-container">
                <div className="profile-header">
                    <h1 className="profile-title">👤 Profile</h1>
                    <p className="profile-subtitle">Manage your account</p>
                </div>

                <div className="profile-card">
                    {/* Avatar */}
                    <div className="profile-avatar-section">
                        <div className="profile-avatar">
                            {profile.profile_picture ? (
                                <img 
                                    src={profile.profile_picture} 
                                    alt="Profile"
                                    onError={(e) => {
                                        e.target.style.display = 'none';
                                        e.target.nextSibling.style.display = 'flex';
                                    }}
                                />
                            ) : null}
                            <div className={`avatar-placeholder ${profile.profile_picture ? 'hidden' : ''}`}>
                                {profile.first_name?.[0] || profile.username?.[0] || 'U'}
                            </div>
                        </div>
                        <div className="profile-badge">
                            <span className={`badge ${profile.user_type}`}>
                                {profile.user_type_display}
                            </span>
                            {profile.email_verified && (
                                <span className="badge verified">✓ Verified</span>
                            )}
                        </div>
                    </div>

                    {/* Info Grid */}
                    <div className="profile-info-grid">
                        <div className="info-grid">
                            <div className="info-item">
                                <label>Username</label>
                                <p>{profile.username}</p>
                            </div>
                            <div className="info-item">
                                <label>Email</label>
                                <p>{profile.email}</p>
                            </div>
                            <div className="info-item">
                                <label>First Name</label>
                                <p>{profile.first_name || "—"}</p>
                            </div>
                            <div className="info-item">
                                <label>Last Name</label>
                                <p>{profile.last_name || "—"}</p>
                            </div>
                            <div className="info-item">
                                <label>Phone</label>
                                <p>{profile.phone_number || "—"}</p>
                            </div>
                            <div className="info-item">
                                <label>Birth</label>
                                <p>{profile.date_of_birth ? new Date(profile.date_of_birth).toLocaleDateString() : "—"}</p>
                            </div>
                            <div className="info-item">
                                <label>Joined</label>
                                <p>{new Date(profile.created_at).toLocaleDateString()}</p>
                            </div>
                            <div className="info-item">
                                <label>Verified</label>
                                <p className={profile.email_verified ? "status-verified" : "status-pending"}>
                                    {profile.email_verified ? "✅ Yes" : "⏳ No"}
                                </p>
                            </div>
                        </div>

                        {/* Stats */}
                        <div className="stats-grid">
                            <div className="stat-card">
                                <div className="stat-value">{profile.enrolled_courses_count}</div>
                                <div className="stat-label">Enrolled</div>
                            </div>
                            <div className="stat-card">
                                <div className="stat-value">{profile.completed_courses_count}</div>
                                <div className="stat-label">Completed</div>
                            </div>
                            <div className="stat-card">
                                <div className="stat-value">{profile.average_progress}%</div>
                                <div className="stat-label">Progress</div>
                            </div>
                            {profile.user_type === "instructor" && (
                                <>
                                    <div className="stat-card">
                                        <div className="stat-value">{profile.instructor_rating}</div>
                                        <div className="stat-label">Rating</div>
                                    </div>
                                    <div className="stat-card">
                                        <div className="stat-value">{profile.total_students}</div>
                                        <div className="stat-label">Students</div>
                                    </div>
                                </>
                            )}
                        </div>

                        {/* Bio */}
                        {profile.bio && (
                            <div className="bio-section">
                                <p>{profile.bio}</p>
                            </div>
                        )}
                    </div>

                    {/* Actions */}
                    <div className="profile-actions">
                        <button className="btn-primary" onClick={() => setIsEditing(true)}>
                            ✏️ Edit
                        </button>
                        <button className="btn-secondary" onClick={() => setIsChangingPassword(true)}>
                            🔑 Password
                        </button>
                    </div>
                </div>
            </div>
            <Footer />
        </div>
    );
}

export default Profile;